import { Component, Element, h, Host, Prop, State, Watch, Event, EventEmitter, Method } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import { ChipSchemeConfiguration } from "../../constants/colors";
import { ICONSIZE_SMALL } from "../icon/constants/icon-constants";
import { ChipLocalization, ChipRank, ChipSchemeDefinition, ChipSchemeName, ChipSize } from "./constants/chip-types";
import { CHIP_RANK_PRIMARY, CHIP_SCHEME_CONFIG, CHIP_SCHEME_MAP, CHIP_SIZE_NORMAL } from "./constants/chip-constants";
import { CHIP_LOCALIZATION } from "./constants/chip-i18n";
import pfx from "../../utils/style-class-prefix-helper";
import { BackgroundColor } from "../background/constants/background-types";
import { StyledComponent } from "../../decorators/styled-component";

@Component({
  tag: "envc-eon-ui-chip",
  styleUrl: "chip.scss",
  shadow: true
})
export class Checkbox
  implements ColorableComponent<ChipSchemeConfiguration<ChipSchemeDefinition>>, LocalizableComponent<ChipLocalization> {
  schemeConfig: ChipSchemeConfiguration<ChipSchemeDefinition> = CHIP_SCHEME_CONFIG;
  schemeMap = CHIP_SCHEME_MAP;

  localization: Localization<ChipLocalization> = CHIP_LOCALIZATION;
  currentLocalization: ChipLocalization;

  @State() activeScheme: ChipSchemeDefinition;

  /**
   * Raised when the button has been clicked.
   */
  @Event() chipClick: EventEmitter;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEnvcEonUiChipElement;

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: ChipSchemeName;

  /**
   * The label of the chip
   */
  @Prop() label: string;

  /**
   * Set the size of the chip
   */
  @Prop() size: ChipSize = CHIP_SIZE_NORMAL;

  /**
   * Set the rank of the chip (primary or secondary)
   */
  @Prop() rank: ChipRank = CHIP_RANK_PRIMARY;

  /**
   * Whether the chip is disabled
   */
  @Prop() disabled: boolean = false;

  componentWillLoad() {
    this.applyScheme();
    this.setCurrentLocalization();
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  @Watch("scheme")
  applyScheme(): void {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  render() {
    return (
      <Host>
        <button
          type="button"
          class={pfx({ [`size-${this.size}`]: true, [`rank-${this.rank}`]: true })}
          onClick={() => this.chipClick.emit()}
          disabled={this.disabled}
        >
          <span class={pfx("label")}>{this.label}</span>
          <envc-eon-ui-icon aria-label={this.currentLocalization.removeItem} name="close" size={ICONSIZE_SMALL} />
        </button>
      </Host>
    );
  }
}
